import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/providers';
import { queryKeys } from '@/lib/queryClient';
import type { Community } from '@/types';
import { listMyCommunities } from '../api/myCommunities';
import { getCommunitiesByIds } from '../api/communities';
import { leaveCommunity } from '../api/communityView';
import { getRecentCommunityIds } from '../recentCommunities';

/** The signed-in user's owned + joined communities, with latest-activity data. */
export function useMyCommunities() {
  const { profile } = useAuth();
  const userId = profile?.id ?? '';
  return useQuery({
    queryKey: queryKeys.userCommunities(userId),
    queryFn: () => listMyCommunities(userId),
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

/** Leave a community; refreshes the My Communities list on success. */
export function useLeaveCommunity() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  const userId = profile?.id ?? '';
  return useMutation({
    mutationFn: (communityId: string) => leaveCommunity(communityId, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.userCommunities(userId) });
      void qc.invalidateQueries({ queryKey: queryKeys.communities.all });
    },
  });
}

/**
 * Resolves the device-local "recently viewed" ids into community rows, preserving
 * recency order. Read once per mount (localStorage is sync), so the rail reflects
 * wherever the user last navigated.
 */
export function useRecentCommunities() {
  const ids = useMemo(() => getRecentCommunityIds(), []);
  const query = useQuery({
    queryKey: ['communities', 'recent', ids],
    queryFn: () => getCommunitiesByIds(ids),
    enabled: ids.length > 0,
    staleTime: 60_000,
  });

  const communities = useMemo<Community[]>(() => {
    const byId = new Map((query.data ?? []).map((c) => [c.id, c]));
    return ids.map((id) => byId.get(id)).filter((c): c is Community => Boolean(c));
  }, [query.data, ids]);

  return { ...query, communities };
}
